import startCase from 'lodash.startcase';
import { useMemo, useState } from 'react';
import { useCustomerOrder } from '@backpackjs/storefront';

import { Link, Svg } from '../../../snippets';
import { OrdersItemLine } from './OrdersItemLine';

export function OrdersItem({ order }) {
  const { order: shopifyOrder } = useCustomerOrder({
    id: btoa(order.id),
  });

  const orderDate = useMemo(() => {
    const newDate = new Date(order.processedAt);
    return newDate.toLocaleDateString('default', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }, [order.processedAt]);

  const orderStatusUrl = useMemo(() => {
    return order.statusUrl.replace('www.', 'shop.');
  }, [order.statusUrl]);

  const fulfillmentStatus = startCase(order.fulfillmentStatus.toLowerCase());
  const total = `$${parseFloat(order.totalPriceV2.amount).toFixed(2)}`;

  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* mobile */}
      <div className="flex flex-col md:hidden md:gap-3">
        <div
          tabIndex="0"
          role="button"
          className={`flex justify-between border border-[#DDE2E7]  bg-white px-6 py-4 ${
            isOpen ? 'border-b-0' : ''
          }`}
          onClick={() => setIsOpen(!isOpen)}
          onKeyPress={(e) => e.key === 'Enter' && setIsOpen(!isOpen)}
        >
          <span className="text-sm font-light">
            Order:{' '}
            {!isOpen ? (
              order.orderNumber
            ) : (
              <Link
                className="text-underline text-[#3383C5]"
                href={order.detailsUrl}
                newTab
                aria-label={`Go to order page for order ${order.name}`}
              >
                {order.orderNumber}
              </Link>
            )}
          </span>
          {isOpen ? (
            <Svg
              className="w-4 text-current"
              src="/svgs/chevron-up.svg#chevron-up"
              title="Up"
              viewBox="0 0 24 24"
            />
          ) : (
            <Svg
              className="w-4 text-current"
              src="/svgs/chevron-down.svg#chevron-down"
              title="Down"
              viewBox="0 0 24 24"
            />
          )}
        </div>
        {isOpen && (
          <div className="border border-t-0 border-[#DDE2E7] bg-white px-6 py-4">
            <p className="mb-3 text-sm">Date: {orderDate}</p>

            <p className="mb-3 text-sm">
              Order status:{' '}
              <span className="lowercase">{fulfillmentStatus}</span>
            </p>

            <p className="mb-3 text-sm">Total: {total}</p>

            <p className="mb-3 text-sm">
              Tracking:{' '}
              <Link className="text-underline" href={orderStatusUrl} newTab>
                Track order
              </Link>
            </p>

            {shopifyOrder?.lineItems?.map((item) => {
              return <OrdersItemLine lineItem={item} />;
            })}
          </div>
        )}
      </div>

      {/* tablet/desktop */}
      <div className="hidden grid-cols-[2fr_2fr_2fr_2fr_1fr] gap-3 bg-white px-3 py-4 text-sm md:grid">
        <p>
          <Link
            className="text-underline !text-[#202635]"
            href={order.detailsUrl}
            newTab
            aria-label={`Go to order page for order ${order.name}`}
          >
            {order.name}
          </Link>
        </p>

        <p>{orderDate}</p>

        <p className="italic">{fulfillmentStatus}</p>

        <p>{total}</p>

        <p>
          <Link className="text-underline" href={orderStatusUrl} newTab>
            Track order
          </Link>
        </p>
      </div>
    </>
  );
}

OrdersItem.displayName = 'OrdersItem';
